import React, { useEffect, useState } from 'react';
import { useController } from 'react-hook-form';
import { AutoComplete } from 'antd';
import { makeStyles } from '@mui/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import { http } from '@network';
import { ErrorMessage } from '@components/ErrorMessage';

interface Props {
  value: string | number;
  onChange: (value: any) => void;
  error?: string;
  reference: string;
  optionLabel?: string;
  placeholder?: string;
  disabledProp?: string;
  disabled?: boolean;
  title?: string;
}

interface ListOption {
  value: number | string;
  label: string;
  disabled?: boolean;
}

export const AutoCompleteControlledInput = ({
  value, onChange, reference, optionLabel, placeholder, disabledProp, disabled = false, error,
}: Props) => {
  const cls = useStyles();
  const [allItems, setAllItems] = useState<ListOption[]>([]);
  const [filteredItems, setFilteredItems] = useState<ListOption[]>([]);

  useEffect(() => {
    if (!value) {
      setFilteredItems(allItems);
    }
  }, [value]);

  useEffect(() => {
    (async () => {
      const response = await http.get<any>(reference, {
        params: { _start: 0, _end: 9999999 },
      });
      const nextOptions: ListOption[] = [];
      if (response.data && Array.isArray(response.data)) {
        if (typeof response.data[0] === 'string') {
          (response.data ?? []).forEach((model: string) => nextOptions.push({
            value: model,
            label: model,
            disabled: disabledProp === model,
          }));
        } else {
          (response.data ?? []).forEach(model => nextOptions.push({
            value: model[optionLabel ?? 'nameMx'] || model['name'] || model['title'],
            label: model[optionLabel ?? 'nameMx'] || model['name'] || model['title'],
            disabled: disabledProp ? !!model[disabledProp] : undefined,
          }));
        }
      }
      nextOptions.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
      setAllItems(nextOptions);
      setFilteredItems(nextOptions);
    })();
  }, []);

  const onSearch = (searchText: string) => setFilteredItems(searchText
    ? allItems.filter(o => o.label.toLowerCase().includes(searchText.toLowerCase()))
    : allItems,
  );

  const filteredItemsDisabled = filteredItems.map(item => ({
    ...item,
    disabled: item.disabled || value === item.value,
  }));

  const errorMessage = (error as any)?.message?.message;

  return (
    <>
      <AutoComplete
        value={value}
        onChange={text => onChange(text)}
        onClear={() => onChange('')}
        allowClear
        options={filteredItemsDisabled}
        className={cls.autocompleteField}
        notFoundContent="Not found"
        onSelect={text => onChange(text)}
        onSearch={onSearch}
        placeholder={placeholder}
        status={errorMessage ? 'error' : undefined}
        disabled={disabled}
      />
      {errorMessage && <ErrorMessage text={errorMessage} />}
      <ArrowDropDownIcon className={cls.icon} />
    </>
  );
};

const useStyles = makeStyles({
  autocompleteField: {
    borderBottom: '1px solid rgba(0, 42, 119, 0.10) !important',
    width: '100%',
    '& .ant-select-selector': {
      borderColor: 'transparent',
      boxShadow: 'none !important',
      padding: 0,
      border: 'none !important',
      borderRadius: '0 !important',
      outline: 'none',
      fontSize: '15px !important',
    },
    '& .ant-select-selection-search': {
      left: '0 !important',
      right: '0 !important',
      fontSize: '15px !important',
    },
  },
  icon: {
    top: 2,
    color: '#777',
    right: 0,
    display: 'inline-block',
    position: 'absolute',
  },
});
